import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import { OUTREACH_STAGES, OUTREACH_PROMOTE_STAGE } from "../../constants/dealStages";
import { fmt } from "../../utils/formatters";
import { parseDealName } from "../../utils/parseDealName";
import StageBadge from "../common/StageBadge";
import { followUpDays, fmtMoneyShort, fmtShortDate } from "../../services/outreachFlow";

// Pipeline's deal card, for a broker LOI: the offer instead of the ask, the
// broker it went through, whether the broker confirmed receipt and whether the
// seller countered, and when to chase. The stage menu offers the Outreach
// stages plus moving the deal into Pipeline.
export default function OutreachCard({ deal, onClick, photoUrl, onStageChange, onPromote, readOnly = false }) {
  const firstAddress = deal.addresses?.[0] ?? "";
  const extraCount = (deal.addresses?.length ?? 1) - 1;
  const dealName = parseDealName(deal.sourceNotes);
  const [stageMenu, setStageMenu] = useState(false);
  const stageBtnRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  function recalcPos() {
    if (!stageBtnRef.current) return;
    const rect = stageBtnRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }
  useLayoutEffect(() => { if (stageMenu) recalcPos(); }, [stageMenu]);
  useEffect(() => {
    if (!stageMenu) return;
    const handler = () => recalcPos();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => { window.removeEventListener("scroll", handler, true); window.removeEventListener("resize", handler); };
  }, [stageMenu]);

  const offerPsf = deal.offerPrice && deal.squareFeet ? deal.offerPrice / deal.squareFeet : null;
  const due = followUpDays(deal);

  return (
    <div
      onClick={onClick}
      className="bg-[rgba(44,44,44,0.7)] hover:bg-[#2c2c2c] border border-[rgba(58,58,58,0.5)] hover:border-[rgba(233,53,41,0.4)] rounded-xl transition-all group shadow-sm overflow-hidden cursor-pointer"
    >
      {photoUrl && (
        <div className="w-full" style={{ height: 120 }}>
          <img src={photoUrl} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-white leading-snug group-hover:text-[#f04f44] transition-colors truncate">
                {dealName || firstAddress}
              </p>
              {extraCount > 0 && (
                <span className="flex-shrink-0 text-xs bg-[#3a3a3a] text-[#8a8a8a] rounded px-1.5 py-0.5">+{extraCount} more</span>
              )}
            </div>
            {dealName && (
              <p className="text-xs text-[#8a8a8a] truncate mt-0.5">{firstAddress}{extraCount > 0 ? ` +${extraCount} more` : ""}</p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {due != null && due <= 0 && (
              <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-[rgba(233,53,41,0.16)] text-[#f04f44] border border-[rgba(233,53,41,0.34)]"
                title={`Follow up was due ${fmtShortDate(deal.followUpDate)}`}>
                {due === 0 ? "Follow up today" : `Follow-up ${-due}d overdue`}
              </span>
            )}
            {readOnly ? (
              <StageBadge stage={deal.stage} />
            ) : (
              <button ref={stageBtnRef} onClick={() => setStageMenu((v) => !v)}
                className="flex items-center gap-0.5 hover:opacity-80 transition-opacity" title="Change stage">
                <StageBadge stage={deal.stage} />
                <span className="text-[#8a8a8a] text-xs leading-none">▾</span>
              </button>
            )}
            {!readOnly && stageMenu && createPortal(
              <>
                <div className="fixed inset-0 z-[60]" onClick={() => setStageMenu(false)} />
                <div className="fixed z-[61] bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg shadow-xl py-1 w-52"
                  style={{ top: menuPos.top, right: menuPos.right }} onClick={(e) => e.stopPropagation()}>
                  {OUTREACH_STAGES.map((s) => (
                    <button key={s} onClick={() => { onStageChange?.(deal.id, s); setStageMenu(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#3a3a3a] transition-colors ${
                        s === deal.stage ? "text-[#f04f44] font-semibold" : "text-[#b4b4b4]"}`}>
                      {s}
                    </button>
                  ))}
                  <button onClick={() => { onPromote?.(deal); setStageMenu(false); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-[#7cbb99] hover:bg-[#3a3a3a] border-t border-[#3a3a3a] mt-1 pt-2">
                    Move to Pipeline ({OUTREACH_PROMOTE_STAGE}) →
                  </button>
                </div>
              </>,
              document.body
            )}
          </div>
        </div>

        <div className="flex items-baseline justify-between gap-2 mb-4">
          <p className="text-xs text-[#8a8a8a] truncate">
            {[deal.brokerFirm, deal.brokerName].filter(Boolean).join(" · ") || "Broker not set"}
          </p>
          {deal.loiDate && <span className="text-[10px] text-[#8a8a8a] flex-shrink-0">LOI {fmtShortDate(deal.loiDate)}</span>}
        </div>

        <div className="flex items-start justify-between gap-6">
          {deal.squareFeet > 0 && (
            <div>
              <p className="text-xs text-[#8a8a8a] mb-0.5">Size</p>
              <p className="text-sm font-semibold text-[#f4f4f4] whitespace-nowrap">{fmt.sf(deal.squareFeet)}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-[#8a8a8a] mb-0.5">Our Offer</p>
            <p className="text-sm font-semibold text-[#f04f44] whitespace-nowrap">
              {fmtMoneyShort(deal.offerPrice)}{offerPsf ? ` ($${Math.round(offerPsf)}/SF)` : ""}
            </p>
          </div>
          {deal.counterPrice > 0 && (
            <div>
              <p className="text-xs text-[#8a8a8a] mb-0.5">Counter</p>
              <p className="text-sm font-semibold text-[#f4f4f4] whitespace-nowrap">{fmtMoneyShort(deal.counterPrice)}</p>
            </div>
          )}
        </div>

        <div className="mt-3 pt-2 border-t border-[rgba(58,58,58,0.3)] flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
          <span className={deal.confirmedReceiptAt ? "text-[#7cbb99]" : "text-[#6b6b6b]"}>
            {deal.confirmedReceiptAt ? `✓ Receipt ${fmtShortDate(deal.confirmedReceiptAt)}` : "○ No receipt yet"}
          </span>
          <span className={deal.counteredAt ? "text-[#f04f44]" : "text-[#6b6b6b]"}>
            {deal.counteredAt ? `✓ Countered ${fmtShortDate(deal.counteredAt)}` : "○ No counter"}
          </span>
          {due != null && due > 0 && <span className="text-[#8a8a8a]">Follow up {fmtShortDate(deal.followUpDate)}</span>}
        </div>
      </div>
    </div>
  );
}
